import {useState} from 'react';

import {
  AfriexAdminPermission,
  AfriexTransactionFeeType,
} from '@afriex/core/domain/enums';

import {Button} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import ConfirmationModal from '@/components/Modals/ConfirmationModal';
import type {TransactionFee} from '@/types';

import FeeEntry from './FeeEntry';
import FeeEntryModal from './FeeEntryModal';

interface Props {
  overrides: TransactionFee[];
  onSave: (fee: TransactionFee) => void;
  onDelete: (fee: TransactionFee, index: number) => void;
}

interface OverrideToDelete extends TransactionFee {
  index: number;
}

const FeeOverrideSection = ({overrides, onSave, onDelete}: Props) => {
  const handleAdd = () => {
    setEditingFee({type: AfriexTransactionFeeType.OVERRIDE});
  };
  const [editingFee, setEditingFee] = useState<TransactionFee>();
  const [selectedOverride, setSelectedOverride] = useState<OverrideToDelete>();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const openOverrideDelete = (override: TransactionFee, index: number) => {
    setSelectedOverride({...override, index});
    setIsConfirmModalOpen(true);
  };

  return (
    <div className="section">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-lg sm:text-xl font-medium">Fee Overrides</h1>

        <PermissionsProvider
          permission={AfriexAdminPermission.TransactionFeeCreate}
        >
          <Button colorScheme="cyan" onClick={handleAdd}>
            Add
          </Button>
        </PermissionsProvider>
      </div>

      <div className="mb-8 p-4 mt-4 md:mt-0 rounded-md border-2 border-gray-100 dark:border-dark-borderMid w-full sm:w-3/4 dark:bg-gray-900">
        <p className="text-sm sm:text-base mb-2">
          Create fees that will override the base fee. Overrides take the
          following priority:
        </p>
        <ul className="flex flex-col sm:flex-row flex-wrap gap-1 sm:gap-2 text-sm">
          <li>Exact Currency &gt;</li>
          <li>Destination Currency &gt;</li>
          <li>Payment Method on transaction &gt;</li>
          <li>Base Fee</li>
        </ul>
      </div>

      {overrides.map((override, index) => (
        <FeeEntry
          key={JSON.stringify(Object.values(override))}
          fee={override}
          onEdit={(newOverride) => {
            setEditingFee(newOverride);
          }}
          onDelete={() => openOverrideDelete(override, index)}
        />
      ))}

      <FeeEntryModal
        isOpen={!!editingFee}
        onClose={() => setEditingFee(undefined)}
        fee={editingFee}
        onSave={(newOverride) => {
          setEditingFee(undefined);
          if (!newOverride) {
            return;
          } // don't save to list
          onSave?.(newOverride);
        }}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        label="Are you sure you want to delete this fee?"
        cancelAction={() => setIsConfirmModalOpen(false)}
        cancelActionLabel="No"
        confirmAction={() => {
          setIsLoading(true);
          onDelete(
            selectedOverride as TransactionFee,
            selectedOverride?.index as number,
          );
          setTimeout(() => {
            setIsLoading(false);
            setIsConfirmModalOpen(false);
          }, 1000);
        }}
        confirmActionLabel="Yes"
        loadingLabel="Deleting..."
        loading={isLoading}
        title="Confirm to delete"
      />
    </div>
  );
};
export default FeeOverrideSection;
